<template>
  <div>
    <base-button block type="primary" class="m-0" @click="modal = true" :disabled="!enabled">
      {{ $t('seasonTickets.create.btnLabel') }}
    </base-button>

    <modal id="CreateDistributor"
           :show.sync="modal"
           modal-classes="modal-dialog-centered modal-lg"
           showClose @close="resetForm()">
      <template slot="header">
        <h4 class="modal-title">
          {{ $t('seasonTickets.create.titleText') }}
        </h4>
      </template>
      <template>
        <div class="text-center text-muted mb-4">
          <small>{{
              $t('seasonTickets.create.helpText')
            }}</small>
        </div>
        <base-alert v-if="alert" dismissible :dismiss-callback="() => alert=false" type="danger">
          <!-- TODO: add error translation -->
          {{ getActionErrorDetails }}
        </base-alert>
        <form role="form" @submit.prevent="validate()">
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('generic.id') }}</label>
            <div class="col-sm-9">
              <base-input
                type="string"
                name="id"
                v-validate="modelValidations.required"
                :error="getError('id')"
                v-model="formDetails.id">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.duration') }}</label>
            <div class="col-sm-9">
              <base-input
                type="number"
                name="duration"
                v-validate="modelValidations.number"
                :error="getError('duration')"
                v-model="formDetails.duration">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.internal_name') }}</label>
            <div class="col-sm-9">
              <base-input
                type="string"
                name="internal_name"
                v-validate="modelValidations.required"
                :error="getError('internal_name')"
                v-model="formDetails.internal_name">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.internal_description') }}</label>
            <div class="col-sm-9">
              <base-input
                type="string"
                name="internal_description"
                v-validate="modelValidations.required"
                :error="getError('internal_description')"
                v-model="formDetails.internal_description">
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.discount_id') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('discount_id')">
                <el-select
                  name="discount_id"
                  data-vv-as="'discount_id'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.discount_id">
                  <el-option
                    v-for="option in discounts"
                    class="select-primary"
                    :value="option.id"
                    :label="option.id"
                    :key="option.id">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.fare_group_id') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('fare_group_id')">
                <el-select
                  name="fare_group_id"
                  data-vv-as="'fare_group_id'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.fare_group_id">
                  <el-option
                    v-for="option in fareGroups"
                    class="select-primary"
                    :value="option.id"
                    :label="option.description"
                    :key="option.id">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 col-form-label">{{ $t('seasonTickets.details.trait') }}</label>
            <div class="col-sm-9">
              <base-input
                :error="getError('trait')">
                <el-select
                  name="trait"
                  data-vv-as="'trait'"
                  class="select-primary"
                  v-validate="modelValidations.required"
                  v-model="formDetails.trait">
                  <el-option
                    v-for="option in availableTraits"
                    class="select-primary"
                    :value="option"
                    :label="option"
                    :key="option">
                  </el-option>
                </el-select>
              </base-input>
            </div>
          </div>
          <div class="text-center">
            <base-button
              type="primary"
              class="my-4"
              @click="validate()"
              :loading="updating"
            >{{ $t('seasonTickets.create.actionBtnLabel') }}
            </base-button>
          </div>
        </form>
      </template>
    </modal>
  </div>
</template>


<script>
import {BaseAlert, Modal, TagsInput} from 'src/components';
import {DatePicker, Option, Select} from 'element-ui';
import {mapActions, mapState} from "vuex";
import TagsSelectInput from "@/components/Inputs/TagsSelectInput";
import fare_groups from "@/store/modules/fare_groups";

export default {
  name: 'createSeasonTicket',
  components: {
    TagsSelectInput,
    [Option.name]: Option,
    [Select.name]: Select,
    [DatePicker.name]: DatePicker,
    Modal,
    BaseAlert,
    TagsInput
  },
  props: {
    enabled: {
      type: Boolean,
      required: false,
    }
  },
  data() {
    return {
      modal: false,
      alert: false,
      formDetails: {
        id: '',
        duration: 1,
        internal_name: '',
        internal_description: '',
        discount_id: '',
        fare_group_id: '',
        trait: '',
      },
      modelValidations: {
        required: {
          required: true
        },
        number: {
          numeric: true
        },
      },
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < new Date((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate());
        },
      },
    }
  },
  computed: {
    ...mapState({
      loading: state => state.season_tickets.loading,
      updating: state => state.season_tickets.updating,
      error: state => state.season_tickets.error,
      created: state => state.season_tickets.created,
      availableTraits: state => state.season_tickets.availableTraits,
      discounts: state => state.discount.data,
      fareGroups: state => state.fare_groups.data,
    }),
    getActionErrorDetails() {
      return this.error ? this.error.bodyText : null;
    },
  }, methods: {
    ...mapActions(['LoadDiscounts', 'LoadFareGroups', 'CreateSeasonTicket']),
    getError(fieldName) {
      return this.errors.first(fieldName);
    },
    validate() {
      this.$validator.validateAll().then(isValid => {
        if (isValid) {
          this.create()
        }
      });
    },
    create: function () {
      let payload = {
        data: {
          id: this.formDetails.id,
          duration: this.formDetails.duration * 1,
          duration_unit: 'day(s)',
          internal_name: this.formDetails.internal_name,
          internal_description: this.formDetails.internal_description,
          discount_id: this.formDetails.discount_id,
          fare_group_id: this.formDetails.fare_group_id,
          trait: this.formDetails.trait,
        }
      }
      this.CreateSeasonTicket(payload).then(() => {
        if (this.error != null) {
          this.alert = true
          // got error, don't reset form
        } else {
          this.$notify({
            type: 'success',
            message: this.$t('seasonTickets.create.createdNotification', this.created),
            icon: 'tim-icons icon-alert-circle-exc'
          })
          this.$router.push({
            name: 'season_ticket',
            params: {id: this.created.id}
          }).catch(err => {
          })

          this.resetForm()
        }
      });
    },
    resetForm: function () {
      this.modal = false;
      this.alert = false;
      // resetting form
      this.formDetails = {
        id: '',
        duration: 1,
        internal_name: '',
        internal_description: '',
        discount_id: '',
        fare_group_id: '',
        trait: '',
      }
      // reset validation errors
      this.$validator.reset()

    },
  },
  mounted() {
    this.LoadDiscounts();
    this.LoadFareGroups();
  },
}
</script>
